/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, Send, FileText, RefreshCw, KeyRound, FileDown } from 'lucide-react';
import { listFseDocuments, exportFseDocument, downloadFseDocumentXmlUrl } from '@/lib/fseLombardia';
import SignPrescriptionDialog from '@/components/fse/SignPrescriptionDialog';
import { downloadPrescriptionPdf } from '@/components/fse/prescriptionPdf';

const formatDT = (iso) => { try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso || ''; } };

const STATUS_META = {
  draft:     { label: 'Bozza',                variant: 'secondary' },
  signed:    { label: 'Firmata (OTP)',        variant: 'default'   },
  exported:  { label: 'Trasmesso (sim)',      variant: 'success'   },
  confirmed: { label: 'Confermato SISS',      variant: 'default'   },
};

export const DocumentList = ({ mode = 'pro', refreshTick = 0, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');
  const [signDocId, setSignDocId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listFseDocuments({ limit: 50 });
      setItems(r.items || []);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const doExport = async (id) => {
    setExporting(id);
    try {
      await exportFseDocument(id, 'Trasmissione a SISS (simulazione mTLS)');
      toast.success('Documento trasmesso (mTLS simulato)');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio');
    } finally { setExporting(''); }
  };

  const download = (id) => {
    const url = downloadFseDocumentXmlUrl(id);
    window.open(url, '_blank', 'noopener');
  };

  const downloadRxPdf = async (doc) => {
    try {
      // list view strips the XML; we still have the prescription payload.
      await downloadPrescriptionPdf(doc);
    } catch (e) {
      toast.error('Errore generazione PDF');
    }
  };

  return (
    <div className="space-y-3" data-testid="fse-documents-list">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <h2 className="font-display font-semibold">Documenti ({items.length})</h2>
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto" disabled={loading} data-testid="fse-refresh-docs">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {loading && <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={`doc-skel-${i}`} className="h-20 rounded-xl" />)}</div>}
      {!loading && items.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Nessun documento presente.</CardContent></Card>
      )}

      {items.map((doc) => {
        const meta = STATUS_META[doc.status] || STATUS_META.draft;
        return (
          <Card key={doc.id} data-testid={`fse-doc-${doc.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold">{doc.doc_type_label}</div>
                  <div className="text-[11px] text-muted-foreground">LOINC {doc.loinc_code} · creato {formatDT(doc.created_at)}</div>
                  {doc.exported_at && (
                    <div className="text-[11px] text-emerald-600">Trasmesso {formatDT(doc.exported_at)}</div>
                  )}
                  {doc.prescription?.nre && (
                    <div className="text-[11px] mt-0.5">
                      <span className="font-mono font-semibold text-primary">NRE {doc.prescription.nre}</span>
                      {doc.prescription.exemption_code && (
                        <span className="text-muted-foreground"> · esenz. {doc.prescription.exemption_code}</span>
                      )}
                      {doc.prescription.items?.length ? (
                        <span className="text-muted-foreground"> · {doc.prescription.items.length} farmac{doc.prescription.items.length === 1 ? 'o' : 'i'}</span>
                      ) : null}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground font-mono break-all">
                    checksum: {doc.checksum_sha256?.slice(0, 24)}…
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={meta.variant}>{meta.label}</Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => download(doc.id)} data-testid={`fse-download-${doc.id}`}>
                  <Download className="h-3.5 w-3.5 mr-1" /> XML
                </Button>
                {doc.doc_type === 'ricetta_rossa' && (doc.status === 'signed' || doc.status === 'exported') && (
                  <Button size="sm" variant="outline" onClick={() => downloadRxPdf(doc)} data-testid={`fse-pdf-${doc.id}`}>
                    <FileDown className="h-3.5 w-3.5 mr-1" /> PDF ricetta
                  </Button>
                )}
                {mode === 'pro' && doc.doc_type === 'ricetta_rossa' && doc.status === 'draft' && (
                  <Button size="sm" variant="outline" onClick={() => setSignDocId(doc.id)} data-testid={`fse-sign-${doc.id}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Firma OTP
                  </Button>
                )}
                {mode === 'pro' && doc.status !== 'exported' && (doc.doc_type !== 'ricetta_rossa' || doc.status === 'signed') && (
                  <Button size="sm" onClick={() => doExport(doc.id)} disabled={exporting === doc.id} data-testid={`fse-export-${doc.id}`}>
                    {exporting === doc.id ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    Trasmetti a SISS
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <SignPrescriptionDialog
        docId={signDocId}
        open={!!signDocId}
        onOpenChange={(v) => { if (!v) setSignDocId(null); }}
        onSigned={() => { setSignDocId(null); load(); onChanged?.(); }}
      />
    </div>
  );
};

export default DocumentList;
